// api/auth.ts
import axios, { AxiosResponse, AxiosError } from "axios";

const axiosBaseConfig = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_API_PROTOCOL}://${process.env.REACT_APP_BACKEND_API_DOMAIN}/`,
  withCredentials: true,
});

export const checkAuth = async (): Promise<boolean> => {
  try {
    const response = await axiosBaseConfig.get("auth/check");
    return response.status === 200;
  } catch (error) {
    handleApiError(error, "checkAuth");
    return false;
  }
};

export const logout = async (): Promise<boolean> => {
  try {
    const response = await axiosBaseConfig.get("auth/logout");
    return response.status === 200;
  } catch (error) {
    handleApiError(error, "logout");
    return false;
  }
};

type AdminLoginParams = {
  email: string;
  password: string;
  secret: string;
};

export const adminLogin = async ({
  email,
  password,
  secret,
}: AdminLoginParams): Promise<boolean> => {
  try {
    await axiosBaseConfig.post("auth/admin-login", { email, password, secret });
    return true;
  } catch (error) {
    handleApiError(error, "adminLogin");
    return false;
  }
};

export const sendRecoveryEmail = async (email: string): Promise<void> => {
  try {
    await axiosBaseConfig.post("auth/recover-password", { email });
  } catch (error) {
    handleApiError(error, "sendRecoveryEmail");
    throw error;
  }
};

export const confirmPasswordUpdate = async (
  password: string,
  code: string,
): Promise<AxiosResponse> => {
  try {
    return await axiosBaseConfig.post("auth/reset-password", {
      password,
      code,
    });
  } catch (error) {
    handleApiError(error, "confirmPasswordUpdate");
    throw error;
  }
};

/**
 * Centralized API error handler
 * @param error AxiosError object
 * @param context Optional context string for logging (e.g. 'checkAuth')
 */
export const handleApiError = (error: unknown, context?: string): void => {
  const err = error as AxiosError;

  if (err.response) {
    const { status, data } = err.response;

    switch (status) {
      case 400:
        console.error(`[${context}] Bad Request`, data);
        break;
      case 401:
        console.warn(`[${context}] Unauthorized`);
        break;
      case 403:
        console.warn(`[${context}] Forbidden`);
        break;
      case 404:
        console.warn(`[${context}] Not Found`);
        break;
      case 500:
        console.error(`[${context}] Server Error`, data);
        break;
      default:
        console.error(`[${context}] Unhandled status code ${status}`, data);
    }
  } else if (err.request) {
    console.error(`[${context}] No response received`, err.request);
  } else {
    console.error(`[${context}] Error setting up request`, err.message);
  }
};

export default axiosBaseConfig;
